/**
 * In-process holding area for a single pending Power Platform claims challenge.
 *
 * An unsuccessful silent claims acquisition may require user interaction, or
 * may remain blocked by policy even after interaction. AADSTS53003 does not
 * establish that MFA was missing. An explicit `sign_in` can attempt the same
 * challenge through device code without changing policies or permissions.
 *
 * The API layer therefore parks the challenge here instead of discarding it,
 * and `sign_in` redeems it on the user's next explicit Power Platform sign-in.
 *
 * Security posture:
 * - Only a challenge that already passed `validatePowerPlatformClaims` is
 *   accepted, so an untrusted `WWW-Authenticate` header can never put an
 *   arbitrary payload into a token request.
 * - Memory only, one entry, short TTL. Never logged, never written to disk,
 *   never returned to the model or shown to the user.
 * - Read only by the device-code sign-in path, and consumed on read.
 */

import { validatePowerPlatformClaims } from "./power-platform-claims.js";
import { RESOURCES } from "./types.js";
import { GUID_PATTERN } from "../utils/security.js";

/** Bounds staleness: a challenge older than this is not worth prompting for. */
const CHALLENGE_TTL_MS = 15 * 60_000;
const MAX_CONTEXT_CHARS = 120;

/** Exact authentication context that received the service challenge. */
export interface PowerPlatformClaimsBinding {
  clientId: string;
  resource: typeof RESOURCES.POWER_PLATFORM_API;
  account: {
    homeAccountId: string;
    localAccountId: string;
    tenantId: string;
    environment: string;
    username?: string;
  };
}

export interface PendingClaimsChallenge {
  claims: string;
  context: string;
  recordedAtMs: number;
  binding: PowerPlatformClaimsBinding;
}

let pending: PendingClaimsChallenge | null = null;

function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Only public-cloud Power Platform bindings with complete selected identities. */
export function validateClaimsBinding(value: unknown): value is PowerPlatformClaimsBinding {
  if (!object(value) || !object(value.account)) return false;
  const account = value.account;
  return (
    typeof value.clientId === "string" &&
    GUID_PATTERN.test(value.clientId) &&
    value.resource === RESOURCES.POWER_PLATFORM_API &&
    typeof account.tenantId === "string" &&
    GUID_PATTERN.test(account.tenantId) &&
    typeof account.homeAccountId === "string" &&
    /^[\x21-\x7e]{1,512}$/.test(account.homeAccountId) &&
    typeof account.localAccountId === "string" &&
    /^[\x21-\x7e]{1,512}$/.test(account.localAccountId) &&
    typeof account.environment === "string" &&
    /^(?:login\.microsoftonline\.com|login\.windows\.net)$/i.test(account.environment) &&
    (account.username === undefined ||
      (typeof account.username === "string" &&
        account.username.length <= 254 &&
        !/[\p{Cc}\p{Cf}]/u.test(account.username)))
  );
}

/** Display names are not identity. Every security-relevant binding field must match. */
export function claimsBindingsMatch(left: unknown, right: unknown): boolean {
  if (!validateClaimsBinding(left) || !validateClaimsBinding(right)) return false;
  return (
    left.clientId.toLowerCase() === right.clientId.toLowerCase() &&
    left.resource === right.resource &&
    left.account.homeAccountId === right.account.homeAccountId &&
    left.account.localAccountId === right.account.localAccountId &&
    left.account.tenantId === right.account.tenantId &&
    left.account.environment === right.account.environment
  );
}

function copyBinding(binding: PowerPlatformClaimsBinding): PowerPlatformClaimsBinding {
  return {
    clientId: binding.clientId,
    resource: binding.resource,
    account: {
      homeAccountId: binding.account.homeAccountId,
      localAccountId: binding.account.localAccountId,
      tenantId: binding.account.tenantId,
      environment: binding.account.environment,
      ...(binding.account.username === undefined ? {} : { username: binding.account.username }),
    },
  };
}

/** Operation labels are our own literals; keep them printable and bounded anyway. */
function safeContext(context: string): string {
  return context
    .replace(/[\p{Cc}\p{Cf}*`<>[\]]/gu, " ")
    .trim()
    .slice(0, MAX_CONTEXT_CHARS);
}

function live(): PendingClaimsChallenge | null {
  if (
    pending &&
    (Date.now() < pending.recordedAtMs || Date.now() - pending.recordedAtMs > CHALLENGE_TTL_MS)
  )
    pending = null;
  return pending;
}

/**
 * Park a challenge for interactive redemption. Returns false — and stores
 * nothing — for anything that is not the validated CAPOLIDS shape.
 */
export function recordPendingClaimsChallenge(
  claims: string,
  context: string,
  binding: PowerPlatformClaimsBinding,
): boolean {
  if (validatePowerPlatformClaims(claims) === null || !validateClaimsBinding(binding)) return false;
  pending = {
    claims,
    context: safeContext(context),
    recordedAtMs: Date.now(),
    binding: copyBinding(binding),
  };
  return true;
}

/** Atomically consume only the matching challenge, including its label and detached identity snapshot. */
export function takePendingClaimsChallenge(
  binding: PowerPlatformClaimsBinding,
): PendingClaimsChallenge | null {
  const current = live();
  if (!current || !claimsBindingsMatch(current.binding, binding)) return null;
  pending = null;
  return { ...current, binding: copyBinding(current.binding) };
}

/** Whether an unexpired challenge is waiting. Never exposes the payload. */
export function hasPendingClaimsChallenge(binding?: PowerPlatformClaimsBinding): boolean {
  const current = live();
  return (
    current !== null && (binding === undefined || claimsBindingsMatch(current.binding, binding))
  );
}

/** The operation that hit the challenge — for user-facing text, never the claims. */
export function pendingClaimsChallengeContext(binding: PowerPlatformClaimsBinding): string | null {
  const current = live();
  return current && claimsBindingsMatch(current.binding, binding) ? current.context : null;
}

/** Test hook — reset module state between tests. */
export function resetClaimsChallengeStoreForTests(): void {
  pending = null;
}
